import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import { getToken } from "utils/loadAuth";
import { useStyles } from "./style";
import { useTheme } from "@emotion/react";
import PropsTypes from "prop-types";
import OverViewTitle from "components/supplyAndDemand/title";
import LotsStatusTracking from "pages/supplyDemand/lotsStatusTracking";
import { fetchSupplyDemandData } from "pages/supplyDemand/supplyDemand.service";
import { useNavigate, useParams } from "react-router";
import LoadingSpinner from "components/loadingSpinner";
import constants from "../../constants/constants.json";
import Notification from "components/snackbar";
import TakaTable from "./takaTable";

export default function OverviewTable({
  product,
  selectedTaka: selectedTakaProp,
}) {
  const [selectedTakaState, setSelectedTaka] = useState("Taka 3.1");
  const selectedTaka = selectedTakaProp || selectedTakaState;
  const [selectedTakaLoader, setSelectedTakaLoader] = useState(false);
  const lotStatusTableRef = useRef();
  const { pid } = useParams();
  const [stageFilter, setStageFilter] = useState({ id: -1, qty: "" });
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const [tableList, setTableList] = useState();
  const [supplier, setSupplier] = useState([]);
  const [openAlert, setOpenAlert] = useState(false);
  const [alertData, setAlertData] = useState({ message: "", severity: "" });
  const { message, severity } = alertData;
  const navigate = useNavigate();
  const theme = useTheme();
  const classes = useStyles({ theme });

  // Helper functions
  const generateRandomId = () => window.crypto.randomUUID();

  const getValue = (rowKey, value) => {
    if (rowKey === "ADV Item Number") return value || "?";
    if (rowKey === "Manufacturing site") return value || "";
    return value || "-";
  };

  const getClassName = (rowKey) =>
    rowKey === "Manufacturing step" ? classes.tableHead : classes.text;

  const getVariant = (rowKey) =>
    rowKey === "Manufacturing step" ? "head" : "body";

  const renderSupplierHeaders = () =>
    supplier.length > 0 &&
    supplier.map((s, i) => (
      <TableCell
        className={classes.tableHead1}
        key={s}
        id={s}
        sx={
          i === supplier.length - 1 ? { borderRight: "none !important" } : null
        }
        variant="head"
      >
        {s.toUpperCase()}
      </TableCell>
    ));

  const renderTableRows = () => {
    const rows = [
      "Manufacturing step",
      "Manufacturing site",
      "Unit of measure",
      "ADV Item Number",
      "Units",
      "Value (Single unit)",
      "Value (Total)",
    ];
    return rows.map((rowKey) => (
      <TableRow key={rowKey}>
        <TableCell
          className={getClassName(rowKey)}
          component="th"
          scope="row"
          sx={
            rowKey === "Value (Total)"
              ? { borderBottom: "none !important" }
              : {}
          }
          style={
            rowKey === "Manufacturing step"
              ? { minWidth: 155, paddingLeft: "8px" }
              : {}
          }
          variant={getVariant(rowKey)}
        >
          {rowKey}
        </TableCell>
        {tableList?.[rowKey]?.map((m, i) => (
          <TableCell
            key={generateRandomId()}
            className={getClassName(rowKey)}
            sx={{
              ...(i === tableList[rowKey].length - 1
                ? { borderRight: "none !important" }
                : {}),
              ...(rowKey === "Value (Total)"
                ? { borderBottom: "none !important" }
                : {}),
            }}
            style={
              rowKey === "Manufacturing step"
                ? { minWidth: 155, paddingLeft: "8px" }
                : {}
            }
            variant={getVariant(rowKey)}
          >
            {getValue(rowKey, m)}
          </TableCell>
        ))}
      </TableRow>
    ));
  };

  const onSetStageFilter = () => {
    lotStatusTableRef.current.scrollIntoView({ behavior: "smooth" });
  };

  const getVersion = (taka) => {
    if (taka === constants.taka41a) return "v41a";
    if (taka === constants.taka41c) return "v41c";
    return "v31";
  };

  const loadData = useCallback(async () => {
    try {
      const token = await getToken({
        getAccessTokenSilently,
        loginWithRedirect,
      });
      if (!token) return;
      setSelectedTakaLoader(true);
      const version = getVersion(selectedTaka);
      const response = await fetchSupplyDemandData({
        productId: pid,
        token,
        version,
      });
      const data = response?.data;
      if (data) {
        setSelectedTakaLoader(false);
        setTableList(data.values);
      } else {
        setOpenAlert(true);
        setAlertData({
          ...alertData,
          message: constants.errorMessage,
          severity: "error",
        });
      }
    } catch (error) {
      setSelectedTakaLoader(false);
      navigate("/forbidden");
      console.error(error);
    }
  }, [
    pid,
    getAccessTokenSilently,
    loginWithRedirect,
    alertData,
    navigate,
    selectedTaka,
  ]);

  const createData = useCallback(() => {
    setSupplier(tableList?.Supplier?.filter((s) => s));
  }, [tableList]);

  useEffect(() => {
    if (pid) loadData();
  }, [pid, selectedTaka]);

  useEffect(() => {
    if (tableList) createData();
  }, [createData, tableList]);

  if (!tableList) {
    return (
      <Grid className={classes.loadingWrapper}>
        <LoadingSpinner loadingMessage={constants.loading} />
      </Grid>
    );
  }

  let variant = "taka";
  if (selectedTaka === constants.taka41a) {
    variant = "taka41a";
  } else if (selectedTaka === constants.taka41c) {
    variant = "taka41c";
  }

  const TakaTableComponent = (
    <TakaTable
      data-testid="TakaOverviewTable"
      tableList={tableList}
      setStageFilter={setStageFilter}
      stageFilter={stageFilter}
      onSetStageFilter={onSetStageFilter}
      selectedTaka={selectedTaka}
      selectedTakaLoader={selectedTakaLoader}
      variant={variant}
    />
  );

  return (
    <>
      <OverViewTitle
        title={` Overview (${selectedTaka})`}
        theme={theme}
        selectedTaka={selectedTaka}
        setSelectedTaka={setSelectedTaka}
      />
      {product?.product_name === "Taka" ? (
        <>
          {TakaTableComponent}
          <LotsStatusTracking
            lotStatusTableRef={lotStatusTableRef}
            stageFilter={stageFilter}
            setStageFilter={setStageFilter}
            selectedTaka={selectedTaka}
          />
        </>
      ) : (
        <TableContainer
          data-testid="overviewTable"
          component={Paper}
          className={classes.tablebContainer}
        >
          <Table sx={{ width: "100%" }} aria-label="simple table" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell className={classes.tableHead1} variant="head">
                  SUPPLIER
                </TableCell>
                {renderSupplierHeaders()}
              </TableRow>
            </TableHead>
            <TableBody>{renderTableRows()}</TableBody>
          </Table>
          <Notification
            message={message}
            open={openAlert}
            setOpen={setOpenAlert}
            severity={severity}
          />
        </TableContainer>
      )}
    </>
  );
}

OverviewTable.propTypes = {
  product: PropsTypes.any,
  selectedTaka: PropsTypes.string,
};
